import { useEffect, useState } from "react";

import type { AxiosError } from "axios";
import apiV2 from "api-v2";
import { useDispatch } from "react-redux";

import { getUserProfile } from "newui/features/Auth/auth-api";
import { setCurrentUser } from "newui/features/Auth/authSlice";

interface ExecutiveReadOnlyErrorResponse {
	error?: {
		code?: string;
	};
	code?: string;
}

interface UseExecutiveStaleStateReconcileResult {
	isBlocking: boolean;
	dismissBlock: () => void;
}

export const useExecutiveStaleStateReconcile =
	(): UseExecutiveStaleStateReconcileResult => {
		const dispatch = useDispatch();
		const [isBlocking, setIsBlocking] = useState(false);

		useEffect(() => {
			const interceptorId = apiV2.interceptors.response.use(
				(response) => response,
				async (error: AxiosError<ExecutiveReadOnlyErrorResponse>) => {
					const status = error.response?.status;
					const responseData = error.response?.data;
					const errorCode =
						responseData?.error?.code ?? responseData?.code;

					if (
						status === 403 &&
						errorCode === "EXECUTIVE_VIEW_READ_ONLY"
					) {
						try {
							const profileResponse = await getUserProfile();
							dispatch(setCurrentUser(profileResponse.data));
						} catch {
							// Silently swallow refetch errors — the blocking modal
							// is shown regardless so the user can switch back.
						}

						setIsBlocking(true);
					}

					return Promise.reject(error);
				}
			);

			return () => {
				apiV2.interceptors.response.eject(interceptorId);
			};
		}, [dispatch]);

		const dismissBlock = () => {
			setIsBlocking(false);
		};

		return { isBlocking, dismissBlock };
	};
